<template>
  <div class="table-container">
    <table class="table is-narrow user-details">
      <tbody>
        <tr>
          <th class="is-narrow">
            <span class="is-pulled-right">Id</span>
          </th>
          <td>
            <span>{{ user.id }}_LLUser</span>
          </td>

          <th class="is-narrow">
            <span class="is-pulled-right">iOS Version</span>
          </th>
          <td>
            <span>{{ user.iosVersion }}</span>
          </td>
        </tr>
        <tr>
          <th class="is-narrow">
            <span class="is-pulled-right">Created At</span>
          </th>
          <td>
            <span>{{ user.createdAt }}</span>
          </td>

          <th class="is-narrow">
            <span class="is-pulled-right">Android Version</span>
          </th>
          <td>
            <span>{{ user.androidVersion }}</span>
          </td>
        </tr>
        <tr>
          <th class="is-narrow">
            <span class="is-pulled-right">Email</span>
          </th>
          <td>
            <span>{{ user.email }}</span>
          </td>
        </tr>
        <tr>
          <th class="is-narrow">
            <span class="is-pulled-right">Activated</span>
          </th>
          <td>
            <Spinner class="is-pulled-left" width="20" height="20" v-if="isDeactivatingUser" />

            <span v-if="!isDeactivatingUser">
              <span class="tag" :class="activatedTagClass">{{ activatedText }}</span>
              <a v-if="this.user.activated" @click="deactivateUser">deactivate</a>
            </span>

            <span class="has-text-danger" v-if="deactivateError">{{ deactivateError }}</span>
          </td>
        </tr>
        <tr>
          <th class="is-narrow">
            <span class="is-pulled-right">Last Active At</span>
          </th>
          <td>
            <Spinner class="is-pulled-left" width="20" height="20" v-if="isLoadingAccesslog" />
            <span class="has-text-danger" v-if="accessLogLoadError">{{ accessLogLoadError }}</span>
            <span v-if="accessLog">
              {{ accessLog.timestamp }}
            </span>
          </td>
        </tr>
        <tr>
          <th class="is-narrow">
            <span class="is-pulled-right">Phone</span>
          </th>
          <td>
            <span>{{ user.phoneNumber }}</span>
          </td>
        </tr>
        <tr>
          <th class="is-narrow">
            <span class="is-pulled-right">Subscription Map Tiles</span>
          </th>
          <td>
            <a @click="updateSubscriptionMapTiles">
              <span>
                {{ user.subscriptionMapTiles || 'Set' }}
                <i class="icon-edit-2"></i>
              </span>
            </a>
          </td>
        </tr>
        <tr>
          <th class="is-narrow">
            <span class="is-pulled-right">Is Business User</span>
          </th>
          <td>
            <Spinner class="is-pulled-left" width="20" height="20" v-if="isLoadingWhb" />
            <span class="has-text-danger" v-if="whbLoadError">{{ whbLoadError }}</span>
            <div v-if="whb">
              <span class="tag" :class="whbTagClass">{{ whbText }}</span>
              <a @click="updateWhb">
                <span>
                  <i class="icon-edit-2"></i>
                </span>
              </a>
            </div>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Modals -->
    <UserUpdateSubscriptionMapTilesModal
      v-if="showUpdateSubscriptionMapTilesModal"
      :userStorePath="userStorePath"
      @cancelled="closeUpdateSubscriptionMapTilesModal"
      @succeeded="closeUpdateSubscriptionMapTilesModal"
    />

    <UserUpdateWhbModal
      v-if="showUpdateWhbModal"
      :userStorePath="userStorePath"
      :whb="whb"
      @cancelled="closeUpdateWhbModal"
      @succeeded="closeUpdateWhbModal"
    />
  </div>
</template>

<script>
import Spinner from '@/components/utils/Spinner'
import UserUpdateSubscriptionMapTilesModal from './UserUpdateSubscriptionMapTilesModal'
import UserUpdateWhbModal from './UserUpdateWhbModal'

import {
  GET_USER,
  LOAD_ACCESS_LOG,
  IS_LOADING_ACCESS_LOG,
  GET_ACCESS_LOG,
  GET_ACCESS_LOG_LOAD_ERROR,
  DEACTIVATE_USER,
  LOAD_WHB,
  IS_LOADING_WHB,
  GET_WHB,
  GET_WHB_LOAD_ERROR
} from './userStore'

export default {
  name: 'UserInfoTab',

  components: { Spinner, UserUpdateSubscriptionMapTilesModal, UserUpdateWhbModal },

  props: {
    userStorePath: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      showUpdateSubscriptionMapTilesModal: false,
      showUpdateWhbModal: false,
      isDeactivatingUser: false,
      deactivateError: null
    }
  },

  mounted() {
    this.loadAccessLog()
    this.loadWhb()
  },

  methods: {
    updateSubscriptionMapTiles() {
      this.showUpdateSubscriptionMapTilesModal = true
    },

    closeUpdateSubscriptionMapTilesModal() {
      this.showUpdateSubscriptionMapTilesModal = false
    },

    updateWhb() {
      this.showUpdateWhbModal = true
    },

    closeUpdateWhbModal() {
      this.showUpdateWhbModal = false
    },

    loadAccessLog() {
      const loadAccessLogAction = `${this.userStorePath}/${LOAD_ACCESS_LOG}`
      this.$store.dispatch(loadAccessLogAction)
    },

    loadWhb() {
      const loadWhbAction = `${this.userStorePath}/${LOAD_WHB}`
      this.$store.dispatch(loadWhbAction)
    },

    async deactivateUser() {
      this.isDeactivatingUser = true
      this.deactivateError = null

      try {
        const deactivateUserAction = `${this.userStorePath}/${DEACTIVATE_USER}`
        const response = await this.$store.dispatch(deactivateUserAction)

        if (!response.success) {
          this.deactivateError = `Deactivate user failed: ${response.message}`
        }
      } catch (error) {
        this.deactivateError = error || 'Something went wrong.'
      } finally {
        this.isDeactivatingUser = false
      }
    }
  },

  computed: {
    user() {
      const getUser = `${this.userStorePath}/${GET_USER}`
      return this.$store.getters[getUser]
    },

    activatedText() {
      return this.user.activated ? 'yes' : 'no'
    },

    activatedTagClass() {
      return {
        'is-danger': !this.user.activated,
        'is-success': this.user.activated
      }
    },

    whbText() {
      if (this.whb.isBusinessUser) {
        return `yes, until ${this.whb.expires}`
      }
      if (this.whb.expires) {
        return `no, expired ${this.whb.expires}`
      }
      return 'no'
    },

    whbTagClass() {
      return {
        'is-danger': !this.whb.isBusinessUser,
        'is-success': this.whb.isBusinessUser
      }
    },

    isLoadingAccesslog() {
      const isLoadingAccessLog = `${this.userStorePath}/${IS_LOADING_ACCESS_LOG}`
      return this.$store.getters[isLoadingAccessLog]
    },

    accessLog() {
      const getAccessLog = `${this.userStorePath}/${GET_ACCESS_LOG}`
      return this.$store.getters[getAccessLog]
    },

    accessLogLoadError() {
      const getAccessLogLoadError = `${this.userStorePath}/${GET_ACCESS_LOG_LOAD_ERROR}`
      const loadError = this.$store.getters[getAccessLogLoadError]
      if (!loadError) {
        return null
      }

      if (loadError.message && loadError.message.includes('status code 404')) {
        return '[unknown]'
      }

      return 'load failed'
    },

    isLoadingWhb() {
      const isLoadingWhb = `${this.userStorePath}/${IS_LOADING_WHB}`
      return this.$store.getters[isLoadingWhb]
    },

    whb() {
      const getWhb = `${this.userStorePath}/${GET_WHB}`
      return this.$store.getters[getWhb]
    },

    whbLoadError() {
      const getWhbLoadError = `${this.userStorePath}/${GET_WHB_LOAD_ERROR}`
      return this.$store.getters[getWhbLoadError]
    }
  },

  watch: {
    user() {
      this.loadAccessLog()
      this.loadWhb()
    }
  }
}
</script>

<style lang="scss" scoped>
.table {
  th {
    padding-right: 0 !important;
    border-bottom: 0;

    span {
      font-weight: normal;
    }
  }

  td {
    border-bottom: 0;

    span {
      font-weight: bold;
      white-space: nowrap;
    }

    a {
      margin-left: 5px;
    }
  }

  .loader {
    margin-top: 5px;
  }
}
</style>
